import { Box, SvgIconProps, Typography, useTheme } from "@mui/material";
import { ComponentType } from "react";
import { useLocation } from "react-router-dom";
import { useGuardedNavigation } from "../../hooks/use_guarded_navigation";

export const MenuItem = ({
  Icon,
  title,
  link,
  iconFillColor,
  hoverColor,
  onClick,
  isActiveOverride,
  isDisabledOverride,
  testId,
}: {
  Icon: ComponentType<SvgIconProps>;
  title: string;
  link: string;
  iconFillColor?: string;
  hoverColor?: string;
  onClick?: () => void;
  isActiveOverride?: boolean;
  isDisabledOverride?: boolean;
  testId?: string;
}) => {
  const theme = useTheme();
  const location = useLocation();
  const { navigateWithGuards } = useGuardedNavigation();
  const isActive =
    isActiveOverride ??
    (location.pathname.split("/")[1] === link.split("/")[1] ||
      (link === "/run-history" && location.pathname === "/"));
  const isDisabled =
    isDisabledOverride ?? location.pathname.includes("_preview");
  const activate = () => {
    if (isDisabled) return;
    if (onClick) {
      onClick();
      return;
    }
    navigateWithGuards(link);
  };

  // Set default colors
  const defaultIconFillColor = iconFillColor || theme.palette.grey[600];
  const activeIconFillColor = theme.palette.atomic.brandBlueDark;

  return (
    <Box
      data-testid={testId}
      role="button"
      aria-label={title}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activate();
        }
      }}
      sx={{
        textDecoration: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 0.5,
        m: 1,
        backgroundColor: (theme) =>
          isActive ? theme.palette.atomic.blue1 : "transparent",
        border: "1px solid",
        borderColor: isActive ? "#c8d8f5" : "transparent",
        borderRadius: "8px",
        transition: "background-color 0.3s ease, border-color 0.3s ease",
        "&:hover": {
          backgroundColor: (theme) =>
            isActive
              ? theme.palette.atomic.blue1
              : hoverColor || theme.palette.action.hover,
        },
        cursor: isDisabled ? "not-allowed" : "pointer",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "atomic.brandBlue",
          outlineOffset: "-2px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon
          sx={{
            fill: isActive ? activeIconFillColor : defaultIconFillColor,
            transition: "fill 0.3s ease",
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: "10px",
          color: isActive ? activeIconFillColor : defaultIconFillColor,
          transition: "color 0.3s ease",
          whiteSpace: "wrap",
          textAlign: "center",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};
